import React, { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatDOP, formatNum, formatDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DateRangeFilter, { defaultRange30, defaultRange90, buildQuery } from "@/components/DateRangeFilter";

function downloadBlob(blob, fallbackName, response) {
  const cd = response?.headers?.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  const name = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function DgiiDeclarations() {
  const [range, setRange] = useState(defaultRange30());
  const [busy, setBusy] = useState(null);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const qs = buildQuery(range);
      const r = await fetch(`${API}/reports/${kind}${qs}`, { credentials: "include" });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      downloadBlob(await r.blob(), `${kind}.txt`, r);
      toast.success(`Archivo ${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(e.message || "Error generando archivo");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="reports-dgii-filter" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-md p-6" data-testid="dgii-606-card">
          <div className="overline">Formato 606</div>
          <h3 className="text-2xl font-black tracking-tight mt-1 mb-1">Compras de bienes y servicios</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Declaración informativa mensual de compras a proveedores. Incluye RNC, NCF, tipo de bien/servicio,
            fecha, monto facturado e ITBIS pagado. Genera un archivo TXT delimitado por <code>|</code> listo
            para la oficina virtual DGII.
          </p>
          <Button
            onClick={() => download("606")}
            disabled={busy === "606"}
            className="bg-primary font-bold"
            data-testid="download-606-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {busy === "606" ? "Generando…" : "Descargar 606 (TXT)"}
          </Button>
        </div>

        <div className="bg-card border border-border rounded-md p-6" data-testid="dgii-607-card">
          <div className="overline">Formato 607</div>
          <h3 className="text-2xl font-black tracking-tight mt-1 mb-1">Ventas de bienes y servicios</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Declaración informativa mensual de ventas (facturas + notas de crédito y débito). Genera un archivo
            TXT con NCF/e-CF, tipo de identificación del cliente, montos gravados, ITBIS y forma de pago.
          </p>
          <Button
            onClick={() => download("607")}
            disabled={busy === "607"}
            className="bg-primary font-bold"
            data-testid="download-607-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {busy === "607" ? "Generando…" : "Descargar 607 (TXT)"}
          </Button>
        </div>
      </div>

      <div className="mt-4 bg-muted/40 border border-border rounded-md p-4 text-xs text-muted-foreground flex gap-3 items-start">
        <FileText className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
        <div>
          <b className="text-foreground">Cómo declarar:</b> Inicia sesión en la
          <a className="text-primary underline mx-1" href="https://dgii.gov.do/ofv" target="_blank" rel="noreferrer">Oficina Virtual DGII</a>,
          ve a <b>Declaraciones Juradas → IT-1</b>, sube el archivo 606 (Compras) y 607 (Ventas) generados aquí.
          Recuerda configurar tu RNC en <b>Ajustes → Empresa</b>.
        </div>
      </div>
    </>
  );
}

function SalesReport() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(defaultRange30());
  const load = useCallback(async () => {
    const { data } = await api.get(`/reports/sales${buildQuery(range)}`);
    setData(data);
  }, [range]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="reports-sales-filter" />
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div className="kpi-card"><div className="overline">Facturas</div><div className="text-3xl font-black mt-2 data-cell">{data.count}</div></div>
            <div className="kpi-card"><div className="overline">Subtotal</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(data.subtotal || 0)}</div></div>
            <div className="kpi-card"><div className="overline">Total facturado</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(data.total)}</div></div>
            <div className="kpi-card border-primary"><div className="overline text-primary">ITBIS recolectado</div><div className="text-2xl font-black mt-2 data-cell text-success">{formatDOP(data.itbis_total)}</div></div>
          </div>
          <div className="bg-card border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="overline border-b border-border">
                <tr><th className="text-left p-3">Número</th><th className="text-left p-3">NCF</th><th className="text-left p-3">Cliente</th><th className="text-left p-3">Fecha</th><th className="text-right p-3">Subtotal</th><th className="text-right p-3">ITBIS</th><th className="text-right p-3">Total</th></tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id} className="border-b border-border/60 hover:bg-accent">
                    <td className="p-3 data-cell font-bold">{i.number}</td>
                    <td className="p-3 data-cell text-xs">{i.ncf}</td>
                    <td className="p-3">{i.customer_name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{formatDate(i.created_at)}</td>
                    <td className="p-3 text-right data-cell">{formatDOP(i.subtotal || 0)}</td>
                    <td className="p-3 text-right data-cell">{formatDOP(i.itbis || 0)}</td>
                    <td className="p-3 text-right data-cell font-bold">{formatDOP(i.total)}</td>
                  </tr>
                ))}
                {data.invoices.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin facturas en este periodo</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function ItbisReport() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(defaultRange30());
  const load = useCallback(async () => {
    const { data } = await api.get(`/reports/itbis${buildQuery(range)}`);
    setData(data);
  }, [range]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="reports-itbis-filter" />
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="kpi-card" data-testid="kpi-itbis-collected-r">
              <div className="overline">ITBIS recolectado (bruto)</div>
              <div className="text-2xl font-black mt-2 data-cell text-success">{formatDOP(data.itbis_collected_gross)}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.sales.count} facturas</div>
            </div>
            <div className="kpi-card">
              <div className="overline">ITBIS recolectado (neto)</div>
              <div className="text-2xl font-black mt-2 data-cell text-success">{formatDOP(data.itbis_collected_net)}</div>
              <div className="text-xs text-muted-foreground mt-1">– NC {formatDOP(data.credit_notes_itbis)} + ND {formatDOP(data.debit_notes_itbis)}</div>
            </div>
            <div className="kpi-card" data-testid="kpi-itbis-paid-r">
              <div className="overline">ITBIS pagado (compras)</div>
              <div className="text-2xl font-black mt-2 data-cell text-info">{formatDOP(data.itbis_paid)}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.purchases.count} compras</div>
            </div>
            <div className="kpi-card border-primary" data-testid="kpi-itbis-balance-r">
              <div className="overline text-primary">A pagar / a favor</div>
              <div className={`text-2xl font-black mt-2 data-cell ${data.itbis_balance >= 0 ? "text-primary" : "text-success"}`}>{formatDOP(data.itbis_balance)}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.itbis_balance >= 0 ? "Por declarar a DGII" : "Crédito a favor"}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-md overflow-x-auto">
              <div className="px-4 py-3 border-b border-border">
                <div className="overline">Ventas con ITBIS</div>
                <h3 className="text-sm font-bold">Detalle facturas</h3>
              </div>
              <table className="w-full text-xs">
                <thead className="overline border-b border-border">
                  <tr><th className="text-left p-2">NCF</th><th className="text-left p-2">Cliente</th><th className="text-right p-2">Subtotal</th><th className="text-right p-2">ITBIS</th></tr>
                </thead>
                <tbody>
                  {data.sales.items.slice(0, 100).map((i) => (
                    <tr key={i.number} className="border-b border-border/60">
                      <td className="p-2 data-cell">{i.ncf}</td>
                      <td className="p-2 truncate max-w-[180px]">{i.customer_name}</td>
                      <td className="p-2 text-right data-cell">{formatDOP(i.subtotal || 0)}</td>
                      <td className="p-2 text-right data-cell text-success">{formatDOP(i.itbis || 0)}</td>
                    </tr>
                  ))}
                  {data.sales.items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Sin ventas</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="bg-card border border-border rounded-md overflow-x-auto">
              <div className="px-4 py-3 border-b border-border">
                <div className="overline">Compras con ITBIS</div>
                <h3 className="text-sm font-bold">Detalle compras</h3>
              </div>
              <table className="w-full text-xs">
                <thead className="overline border-b border-border">
                  <tr><th className="text-left p-2">Ref.</th><th className="text-left p-2">Suplidor</th><th className="text-right p-2">Subtotal</th><th className="text-right p-2">ITBIS</th></tr>
                </thead>
                <tbody>
                  {data.purchases.items.slice(0, 100).map((p) => (
                    <tr key={p.number} className="border-b border-border/60">
                      <td className="p-2 data-cell">{p.invoice_ref || p.number}</td>
                      <td className="p-2 truncate max-w-[180px]">{p.supplier_name}</td>
                      <td className="p-2 text-right data-cell">{formatDOP(p.subtotal || 0)}</td>
                      <td className="p-2 text-right data-cell text-info">{formatDOP(p.itbis || 0)}</td>
                    </tr>
                  ))}
                  {data.purchases.items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Sin compras</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {data.notes && data.notes.count > 0 && (
            <div className="bg-card border border-border rounded-md overflow-x-auto mt-4">
              <div className="px-4 py-3 border-b border-border">
                <div className="overline">Notas de Crédito / Débito</div>
                <h3 className="text-sm font-bold">Ajustes a ITBIS recolectado</h3>
              </div>
              <table className="w-full text-xs">
                <thead className="overline border-b border-border">
                  <tr><th className="text-left p-2">Tipo</th><th className="text-left p-2">Número</th><th className="text-left p-2">NCF</th><th className="text-left p-2">Factura</th><th className="text-right p-2">Subtotal</th><th className="text-right p-2">ITBIS</th></tr>
                </thead>
                <tbody>
                  {data.notes.items.map((n) => (
                    <tr key={n.number} className="border-b border-border/60">
                      <td className="p-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${n.kind === "credit" ? "bg-amber-500/10 text-amber-600" : "bg-info/10 text-info"}`}>{n.kind === "credit" ? "NC" : "ND"}</span></td>
                      <td className="p-2 data-cell">{n.number}</td>
                      <td className="p-2 data-cell">{n.ncf}</td>
                      <td className="p-2 data-cell">{n.invoice_number}</td>
                      <td className="p-2 text-right data-cell">{formatDOP(n.subtotal || 0)}</td>
                      <td className={`p-2 text-right data-cell font-bold ${n.kind === "credit" ? "text-amber-600" : "text-info"}`}>{n.kind === "credit" ? "−" : "+"}{formatDOP(n.itbis || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function InventoryReport() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/inventory").then(({ data }) => setData(data)); }, []);
  if (!data) return null;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="kpi-card"><div className="overline">Productos</div><div className="text-3xl font-black mt-2 data-cell">{data.products_count}</div></div>
        <div className="kpi-card"><div className="overline">Valor a costo</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(data.total_value_cost)}</div></div>
        <div className="kpi-card"><div className="overline">Valor a precio</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(data.total_value_price)}</div></div>
        <div className="kpi-card border-primary"><div className="overline text-primary">Beneficio potencial</div><div className="text-2xl font-black mt-2 data-cell text-success">{formatDOP(data.potential_profit)}</div></div>
      </div>
    </>
  );
}

function CustomersReport() {
  const [items, setItems] = useState([]);
  const [range, setRange] = useState(defaultRange90());
  const load = useCallback(async () => {
    const { data } = await api.get(`/reports/top-customers${buildQuery(range)}`);
    setItems(data);
  }, [range]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="reports-customers-filter" />
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr><th className="text-left p-3">Cliente</th><th className="text-right p-3">Facturas</th><th className="text-right p-3">Total facturado</th></tr>
          </thead>
          <tbody>
            {items.map((c, i) => (
              <tr key={i} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 font-semibold">{c.customer_name}</td>
                <td className="p-3 text-right data-cell">{c.count}</td>
                <td className="p-3 text-right data-cell font-bold">{formatDOP(c.total)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">Sin datos en este periodo</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function Reports() {
  return (
    <PageContainer>
      <PageHeader title="Reportes" subtitle="Análisis de ventas, ITBIS, inventario y clientes con rangos de fechas." testid="reports-header" />
      <Tabs defaultValue="sales">
        <TabsList className="mb-4 flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="sales" data-testid="tab-sales">Ventas</TabsTrigger>
          <TabsTrigger value="sales-by-unit" data-testid="tab-sales-by-unit">Ventas por Unidad</TabsTrigger>
          <TabsTrigger value="itbis" data-testid="tab-itbis">ITBIS</TabsTrigger>
          <TabsTrigger value="dgii" data-testid="tab-dgii">Declaraciones DGII</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventario</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Top Clientes</TabsTrigger>
        </TabsList>
        <TabsContent value="sales"><SalesReport /></TabsContent>
        <TabsContent value="sales-by-unit"><SalesByUnitReport /></TabsContent>
        <TabsContent value="itbis"><ItbisReport /></TabsContent>
        <TabsContent value="dgii"><DgiiDeclarations /></TabsContent>
        <TabsContent value="inventory"><InventoryReport /></TabsContent>
        <TabsContent value="customers"><CustomersReport /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function SalesByUnitReport() {
  const [range, setRange] = useState(defaultRange30());
  const [onlyMulti, setOnlyMulti] = useState(false);
  const [data, setData] = useState({ products: [], totals: { revenue: 0, cost: 0, margin: 0, margin_pct: 0, product_count: 0 } });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.date_from) params.set("date_from", range.date_from);
      if (range.date_to) params.set("date_to", range.date_to);
      if (onlyMulti) params.set("only_multi_unit", "true");
      const { data } = await api.get(`/reports/sales-by-unit?${params}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  }, [range, onlyMulti]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const exportCsv = () => {
    const rows = [["SKU", "Producto", "Unidad", "Cantidad", "Equiv. base", "Precio prom.", "Ingresos", "Costo", "Margen", "Margen %"]];
    data.products.forEach((p) => {
      p.units.forEach((u) => {
        rows.push([p.sku, p.name, u.unit, u.qty_sold, u.qty_base, u.avg_price, u.revenue, u.cost, u.margin, u.margin_pct]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ventas_por_unidad_${range.date_from || "todo"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="sales-by-unit-report">
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <DateRangeFilter value={range} onChange={setRange} testidPrefix="sbu-filter" />
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={onlyMulti}
            onChange={(e) => setOnlyMulti(e.target.checked)}
            data-testid="sbu-multi-only"
          />
          Solo productos con múltiples unidades
        </label>
        <Button onClick={exportCsv} variant="outline" size="sm" className="ml-auto" data-testid="sbu-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="kpi-card"><div className="overline">Productos vendidos</div><div className="text-2xl font-black mt-1 data-cell">{data.totals.product_count}</div></div>
        <div className="kpi-card"><div className="overline">Ingresos totales</div><div className="text-2xl font-black mt-1 data-cell">{formatDOP(data.totals.revenue)}</div></div>
        <div className="kpi-card"><div className="overline">Costo total</div><div className="text-2xl font-black mt-1 data-cell">{formatDOP(data.totals.cost)}</div></div>
        <div className="kpi-card border-primary">
          <div className="overline text-primary">Margen bruto</div>
          <div className="text-2xl font-black mt-1 data-cell text-success">{formatDOP(data.totals.margin)}</div>
          <div className="text-xs text-muted-foreground mt-1">{data.totals.margin_pct}%</div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-2 w-6"></th>
              <th className="text-left p-2">Producto</th>
              <th className="text-left p-2">SKU</th>
              <th className="text-right p-2">Stock actual</th>
              <th className="text-right p-2">Unidades vendidas</th>
              <th className="text-right p-2">Ingresos</th>
              <th className="text-right p-2">Margen</th>
              <th className="text-right p-2">%</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Cargando…</td></tr>}
            {!loading && data.products.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin ventas en el período.</td></tr>}
            {!loading && data.products.map((p) => (
              <React.Fragment key={p.product_id}>
                <tr
                  className="border-b border-border/60 hover:bg-accent cursor-pointer"
                  onClick={() => toggle(p.product_id)}
                  data-testid={`sbu-row-${p.product_id}`}
                >
                  <td className="p-2 text-center text-muted-foreground">{expanded.has(p.product_id) ? "▾" : "▸"}</td>
                  <td className="p-2">
                    <div className="font-semibold">{p.name}</div>
                    {p.sale_units_configured > 0 && p.units.length > 1 && (
                      <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded uppercase">{p.units.length} unidades</span>
                    )}
                  </td>
                  <td className="p-2 data-cell text-xs">{p.sku}</td>
                  <td className="p-2 text-right data-cell">{p.current_stock} {p.base_unit}</td>
                  <td className="p-2 text-right data-cell">
                    {p.units.map((u, i) => (
                      <span key={i} className="inline-block bg-muted px-1.5 py-0.5 rounded text-[11px] mr-1">
                        {u.qty_sold} {u.unit}
                      </span>
                    ))}
                  </td>
                  <td className="p-2 text-right data-cell font-bold">{formatDOP(p.totals.revenue)}</td>
                  <td className="p-2 text-right data-cell text-success">{formatDOP(p.totals.margin)}</td>
                  <td className="p-2 text-right data-cell">{p.totals.margin_pct}%</td>
                </tr>
                {expanded.has(p.product_id) && (
                  <tr className="bg-muted/20">
                    <td></td>
                    <td colSpan={7} className="p-0">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground border-b border-border/40">
                            <th className="text-left px-2 py-1">Unidad</th>
                            <th className="text-right px-2 py-1">Cantidad</th>
                            <th className="text-right px-2 py-1">Equiv. base</th>
                            <th className="text-right px-2 py-1">Precio prom.</th>
                            <th className="text-right px-2 py-1">Ingresos</th>
                            <th className="text-right px-2 py-1">Costo</th>
                            <th className="text-right px-2 py-1">Margen</th>
                            <th className="text-right px-2 py-1">%</th>
                            <th className="text-right px-2 py-1">Facturas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.units.map((u, i) => (
                            <tr key={i} className={u.is_base ? "" : "text-primary"}>
                              <td className="px-2 py-1 font-semibold">
                                {u.is_base ? u.unit : <><span className="text-[9px] bg-primary/10 px-1 rounded mr-1">Suelto</span>{u.unit}</>}
                              </td>
                              <td className="px-2 py-1 text-right data-cell">{u.qty_sold}</td>
                              <td className="px-2 py-1 text-right data-cell text-muted-foreground">{u.qty_base} {p.base_unit}</td>
                              <td className="px-2 py-1 text-right data-cell">{formatDOP(u.avg_price)}</td>
                              <td className="px-2 py-1 text-right data-cell">{formatDOP(u.revenue)}</td>
                              <td className="px-2 py-1 text-right data-cell">{formatDOP(u.cost)}</td>
                              <td className="px-2 py-1 text-right data-cell">{formatDOP(u.margin)}</td>
                              <td className="px-2 py-1 text-right data-cell">{u.margin_pct}%</td>
                              <td className="px-2 py-1 text-right data-cell">{u.invoices_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
